import React from "react";
import { motion } from "framer-motion";
import {
  Dumbbell,
  Flame,
  Footprints,
  Scale,
  Percent,
  Zap,
  Droplets,
  BatteryCharging,
  Sparkles,
} from "lucide-react";

const DEMO_KINGDOM_SCORE = 87; // illustrative dashboard preview value
const RING_RADIUS = 52; // SVG radius for the score ring

const KingdomRing = ({ score = DEMO_KINGDOM_SCORE }) => {
  const r = RING_RADIUS;
  const c = 2 * Math.PI * r;
  const offset = c - (score / 100) * c;
  return (
    <div className="relative flex h-full flex-col justify-between">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Kingdom Score</span>
        <span className="rounded-full border border-gold/30 bg-gold/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-gold">
          Elite
        </span>
      </div>
      <div className="flex items-center justify-center py-2">
        <div className="relative h-40 w-40">
          <svg className="h-full w-full -rotate-90" viewBox="0 0 120 120">
            <circle cx="60" cy="60" r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="8" />
            <motion.circle
              cx="60"
              cy="60"
              r={r}
              fill="none"
              stroke="url(#goldGrad)"
              strokeWidth="8"
              strokeLinecap="round"
              strokeDasharray={c}
              initial={{ strokeDashoffset: c }}
              whileInView={{ strokeDashoffset: offset }}
              viewport={{ once: true }}
              transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
            />
            <defs>
              <linearGradient id="goldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#f3e5ab" />
                <stop offset="100%" stopColor="#aa8c2c" />
              </linearGradient>
            </defs>
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="font-heading text-4xl font-semibold text-white">{score}</span>
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">of 100</span>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 text-center">
        {[
          ["Training", "92"],
          ["Nutrition", "84"],
          ["Recovery", "85"],
        ].map(([l, v]) => (
          <div key={l} className="rounded-lg border border-white/5 bg-white/[0.02] py-2">
            <p className="font-heading text-base text-white">{v}</p>
            <p className="text-[9px] uppercase tracking-wider text-zinc-500">{l}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, unit, sub, accent }) => (
  <div className="flex h-full flex-col justify-between rounded-2xl border border-white/10 bg-[#0a0a0a] p-4">
    <div className="flex items-center justify-between">
      <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">{label}</span>
      <Icon className={`h-4 w-4 ${accent ? "text-gold" : "text-zinc-500"}`} strokeWidth={1.6} />
    </div>
    <div className="mt-3">
      <span className="font-heading text-2xl font-semibold text-white">{value}</span>
      {unit && <span className="ml-1 text-xs text-zinc-500">{unit}</span>}
      {sub && <p className="mt-0.5 text-[10px] text-zinc-600">{sub}</p>}
    </div>
  </div>
);

export default function DashboardPreview() {
  return (
    <div
      data-testid="dashboard-preview"
      className="glass-strong rounded-3xl p-4 shadow-2xl gold-glow md:p-6"
    >
      <div className="mb-4 flex items-center gap-1.5 px-1">
        <span className="h-2.5 w-2.5 rounded-full bg-white/10" />
        <span className="h-2.5 w-2.5 rounded-full bg-white/10" />
        <span className="h-2.5 w-2.5 rounded-full bg-white/10" />
        <span className="ml-3 text-[10px] uppercase tracking-[0.2em] text-zinc-600">Jollof Fit Kingdom · Dashboard</span>
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <div className="col-span-2 row-span-2 rounded-2xl border border-gold/20 bg-gradient-to-b from-[#12100a] to-[#0a0a0a] p-5">
          <KingdomRing score={DEMO_KINGDOM_SCORE} />
        </div>
        <StatCard icon={Dumbbell} label="Workout" value="58" unit="min" sub="Upper body" accent />
        <StatCard icon={Flame} label="Nutrition" value="2,140" unit="kcal" sub="P 168g" />
        <StatCard icon={Footprints} label="Steps" value="11,204" sub="+18% vs avg" />
        <StatCard icon={Scale} label="Weight" value="78.4" unit="kg" sub="-0.6 kg" />
        <StatCard icon={Percent} label="Body Fat" value="14.2" unit="%" sub="-0.3%" accent />
        <StatCard icon={Zap} label="Muscle" value="41.8" unit="kg" sub="+0.2 kg" />
        <StatCard icon={Droplets} label="Hydration" value="62" unit="%" sub="On track" />
        <StatCard icon={BatteryCharging} label="Recovery" value="85" unit="%" sub="Ready" accent />
        <div className="col-span-2 rounded-2xl border border-white/10 bg-[#0a0a0a] p-4 md:col-span-4">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gold/30 bg-gold/10">
              <Sparkles className="h-4 w-4 text-gold" strokeWidth={1.6} />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-gold">Health Insight</p>
              <p className="mt-1 text-sm text-zinc-300">
                Your recovery is peaking — an ideal day for a high-intensity session. Muscle mass is trending up while
                body fat declines.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
